import React, { useEffect, useState } from "react";
import { Prompt, useLocation } from "react-router-dom";
import AddressCard from "./components/AddressCard";
import ContactCard, {
  getContactValidationErrors,
  getPersistedContactMethods,
} from "./components/ContactCard";
import OrganisationMembersCard from "./components/OrganisationMembersCard";
import ServiceEnrollmentCard, {
  createServiceEnrollmentsForParty,
  ServiceRoleSelections,
} from "./components/ServiceEnrollmentCard";
import { useAuth } from "../../../context/AuthContext";
import { useStaticData } from "../../../context/StaticDataContext";
import {
  ContactMethod,
  ContactPreference,
  OrganisationDetails,
  PartyAddress,
  PartyRelationshipSummary,
} from "../../types/types";
import { api } from "../../../api/apiClient";
import { notify } from "../../components-ui/toast";
import "../../../css/forms.css";
import "../../../css/layout.css";
import "../../../css/buttons.css";
import { PartyFormMode, usePartyFormContext } from "./PartyFormContext";

interface OrganisationType {
  organisationTypeId: number;
  name: string;
}

interface PartyOrganisationProps {
  partyId: number | null;
  resetSignal: number;
  onSaved?: (partyId: number) => void;
  onCancel?: () => void;
  showCancel?: boolean;
  saveLabel?: string;
  cancelLabel?: string;
  initialDetails?: OrganisationDetails;
  onCompleted?: () => void;
  organisationEndpoint?: string;
  showServiceEnrollment?: boolean;
  relationshipApiBase?: string;
  organisationOptions?: Array<{ value: number; label: string }>;
  selectedOrganisationId?: number | null;
  onOrganisationSelect?: (organisationId: number) => void;
  showOrganisationMembers?: boolean;
}

/* =========================
   Initial State
========================= */

const initialOrganisationDetails: OrganisationDetails = {
  partyId: 0,
  displayName: "",
  registrationNumber: "",
  organisationTypeId: 0,
  notes: "",
  partyAddresses: [],
  contactMethods: [],
  contactPreferences: [],
  partyContactPreferences: [],
  partyRelationships: [],
  members: [],
};

const toComparableOrganisation = (
  details: OrganisationDetails,
): OrganisationDetails => ({
  ...details,
  partyAddresses: details.partyAddresses ?? [],
  contactMethods: getPersistedContactMethods(details.contactMethods ?? []),
  contactPreferences: details.contactPreferences ?? [],
  // `members` and `partyContactPreferences` are UI mirrors; compare persisted fields.
  partyRelationships: details.partyRelationships ?? [],
  partyContactPreferences: details.contactPreferences ?? [],
  members: details.partyRelationships ?? [],
});

const PartyOrganisation: React.FC<PartyOrganisationProps> = ({
  partyId,
  resetSignal,
  onSaved,
  onCancel,
  showCancel = false,
  saveLabel = "Save",
  cancelLabel = "Cancel",
  initialDetails,
  onCompleted,
  organisationEndpoint,
  showServiceEnrollment = true,
  relationshipApiBase,
  organisationOptions,
  selectedOrganisationId,
  onOrganisationSelect,
  showOrganisationMembers = true,
}) => {
  const partyContext = usePartyFormContext();
  const location = useLocation();
  const [formMode, setFormMode] = useState<"NEW" | "UPDATE">("NEW");

  const { data } = useStaticData();
  const { selectedTenant } = useAuth();
  const organisationTypes: OrganisationType[] = data?.organisationTypes ?? [];
  const contactTypes = data?.contactTypes ?? [];
  const tenantId = selectedTenant?.tenantId;
  const [organisationDetails, setOrganisationDetails] =
    useState<OrganisationDetails>(initialOrganisationDetails);
  const [originalOrganisation, setOriginalOrganisation] =
    useState<OrganisationDetails>(initialOrganisationDetails);

  const [loading, setLoading] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [suppressUnsavedPrompt, setSuppressUnsavedPrompt] = useState(false);
  const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);
  const [originalSelectedServiceIds, setOriginalSelectedServiceIds] = useState<
    number[]
  >([]);
  const [selectedServiceRoles, setSelectedServiceRoles] =
    useState<ServiceRoleSelections>({});
  const [originalSelectedServiceRoles, setOriginalSelectedServiceRoles] =
    useState<ServiceRoleSelections>({});
  const registrationServiceIdValue = Number(
    new URLSearchParams(location.search).get("serviceId"),
  );
  const registrationServiceId = Number.isFinite(registrationServiceIdValue) &&
    registrationServiceIdValue > 0
    ? registrationServiceIdValue
    : partyContext.serviceId ?? null;
  const enrollmentSource =
    new URLSearchParams(location.search).get("source") === "Manual"
      ? "Manual"
      : registrationServiceId
        ? "Registration"
        : "Manual";

  /* =========================
     Dirty check
  ========================= */

  useEffect(() => {
    const organisationDirty =
      JSON.stringify(toComparableOrganisation(organisationDetails)) !==
      JSON.stringify(toComparableOrganisation(originalOrganisation));
    const servicesDirty =
      formMode === "NEW" &&
      JSON.stringify([...selectedServiceIds].sort()) !==
        JSON.stringify([...originalSelectedServiceIds].sort());
    const rolesDirty =
      formMode === "NEW" &&
      JSON.stringify(selectedServiceRoles) !==
        JSON.stringify(originalSelectedServiceRoles);

    const agreementsDirty =
      partyContext.mode !== PartyFormMode.Admin &&
      Boolean(partyContext.agreementResponses?.some((item) => item.accepted !== null));

    setIsDirty(
      organisationDirty || servicesDirty || rolesDirty || agreementsDirty,
    );
  }, [
    formMode,
    organisationDetails,
    originalOrganisation,
    originalSelectedServiceIds,
    originalSelectedServiceRoles,
    selectedServiceIds,
    selectedServiceRoles,
    partyContext.agreementResponses,
    partyContext.mode,
  ]);

  /* =========================
     Warn before leaving
  ========================= */

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!isDirty) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  /* =========================
     Reset
  ========================= */

  useEffect(() => {
    setOrganisationDetails(initialOrganisationDetails);
    setOriginalOrganisation(initialOrganisationDetails);
    setSelectedServiceIds([]);
    setOriginalSelectedServiceIds([]);
    setSelectedServiceRoles({});
    setOriginalSelectedServiceRoles({});
    setFormMode("NEW");
  }, [resetSignal]);

  /* =========================
     Load
  ========================= */

  useEffect(() => {
    if (!initialDetails) return;
    const safeInitialDetails = {
      ...initialDetails,
    };
    setOrganisationDetails(safeInitialDetails);
    setOriginalOrganisation(structuredClone(safeInitialDetails));
    setFormMode("UPDATE");
  }, [initialDetails]);

  useEffect(() => {
    if (!partyId) return;

    const loadOrganisation = async () => {
      try {
        setLoading(true);

        const data = await api<OrganisationDetails>(
          organisationEndpoint ?? `/api/Organisation/${partyId}`,
        );

        const safeData: OrganisationDetails = {
          ...data,
          partyAddresses: data.partyAddresses ?? [],
          contactMethods: data.contactMethods ?? [],
          contactPreferences: data.contactPreferences ?? [],
          partyContactPreferences:
            data.partyContactPreferences ?? data.contactPreferences ?? [],
          partyRelationships: data.partyRelationships ?? data.members ?? [],
          members: data.members ?? data.partyRelationships ?? [],
        };

        setOrganisationDetails(safeData);
        setOriginalOrganisation(structuredClone(safeData));
        setFormMode("UPDATE");
      } catch (error) {
        console.error("Failed to load organisation", error);
      } finally {
        setLoading(false);
      }
    };

    loadOrganisation();
  }, [organisationEndpoint, partyId]);

  /* =========================
     Handlers
  ========================= */

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;

    setOrganisationDetails((prev) => ({
      ...prev,
      [name]:
        name === "organisationTypeId"
          ? value === ""
            ? 0
            : Number(value)
          : value,
    }));
  };

  const handleAddressChange = (partyAddresses: PartyAddress[] = []) => {
    setOrganisationDetails((prev) => ({ ...prev, partyAddresses }));
  };

  const handleContactChange = (contactMethods: ContactMethod[] = []) => {
    setOrganisationDetails((prev) => ({ ...prev, contactMethods }));
  };

  const handleConsentChange = (
    contactPreferences: ContactPreference[] = [],
  ) => {
    setOrganisationDetails((prev) => ({
      ...prev,
      contactPreferences,
      partyContactPreferences: contactPreferences,
    }));
  };

  const handlePartyRelationshipsChange = (
    partyRelationships: PartyRelationshipSummary[] = [],
    persisted = false,
  ) => {
    setOrganisationDetails((prev) => ({
      ...prev,
      partyRelationships,
      members: partyRelationships,
    }));

    if (persisted) {
      setOriginalOrganisation((prev) => ({
        ...prev,
        partyRelationships,
        members: partyRelationships,
      }));
    }
  };

  /* =========================
     Save
  ========================= */

  const handleSave = async () => {
    if (!isDirty) return;

    if (
      !organisationDetails.displayName.trim() ||
      !organisationDetails.organisationTypeId ||
      !organisationDetails.notes?.trim()
    ) {
      notify.error(
        "Organisation name, organisation type and notes are required.",
      );
      return;
    }

    const contactErrors = getContactValidationErrors(
      organisationDetails.contactMethods,
      contactTypes,
    );

    if (contactErrors.length > 0) {
      notify.error("Please correct invalid email addresses or mobile numbers before saving.");
      return;
    }

    try {
      const payload: OrganisationDetails = {
        ...organisationDetails,
        partyAddresses: organisationDetails.partyAddresses ?? [],
        contactMethods: getPersistedContactMethods(
          organisationDetails.contactMethods,
        ),
        contactPreferences: organisationDetails.contactPreferences ?? [],
        partyContactPreferences: organisationDetails.contactPreferences ?? [],
        partyRelationships: organisationDetails.partyRelationships ?? [],
        members: organisationDetails.partyRelationships ?? [],
      };

      let savedOrganisation: OrganisationDetails;

      if (partyContext.mode === PartyFormMode.PublicRegistration) {
        await api<void>("/api/public/register", {
          method: "POST",
          auth: "none",
          body: {
            partyType: "Organisation",
            serviceId: registrationServiceId,
            organisation: payload,
            agreementResponses: partyContext.agreementResponses ?? [],
          },
        });
        notify.success("Registration completed successfully");
        setSuppressUnsavedPrompt(true);
        setIsDirty(false);
        onCompleted?.();
        return;
      }

      if (partyContext.mode === PartyFormMode.ProfileCompletion) {
        await api<void>(
          `/api/public/profile/${encodeURIComponent(partyContext.invitationToken ?? "")}`,
          {
            method: "PUT",
            auth: "none",
            body: {
              organisation: payload,
              agreementResponses: partyContext.agreementResponses ?? [],
            },
          },
        );
        notify.success("Profile completed successfully");
        setSuppressUnsavedPrompt(true);
        setOriginalOrganisation(structuredClone(organisationDetails));
        setIsDirty(false);
        onCompleted?.();
        return;
      }

      if (formMode === "NEW") {
        savedOrganisation = await api("/api/Organisation", {
          method: "POST",
          body: payload,
        });

        if (selectedServiceIds.length > 0) {
          await createServiceEnrollmentsForParty(
            savedOrganisation.partyId,
            selectedServiceIds,
            enrollmentSource,
            selectedServiceRoles,
          );
        }

        notify.success("Organisation created successfully");
        setFormMode("UPDATE");
      } else {
        savedOrganisation = await api(
          organisationEndpoint ?? `/api/Organisation/${organisationDetails.partyId}`,
          {
            method: "PUT",
            body: payload,
          },
        );

        notify.success("Organisation updated successfully");
      }

      const safeSaved: OrganisationDetails = {
        ...savedOrganisation,
        partyAddresses:
          savedOrganisation.partyAddresses?.length || !payload.partyAddresses?.length
            ? savedOrganisation.partyAddresses ?? []
            : payload.partyAddresses ?? [],
        contactMethods:
          savedOrganisation.contactMethods?.length || !payload.contactMethods?.length
            ? savedOrganisation.contactMethods ?? []
            : payload.contactMethods ?? [],
        contactPreferences:
          savedOrganisation.contactPreferences?.length ||
          !payload.contactPreferences?.length
            ? savedOrganisation.contactPreferences ?? []
            : payload.contactPreferences ?? [],
        partyContactPreferences:
          savedOrganisation.partyContactPreferences ??
          savedOrganisation.contactPreferences ??
          payload.contactPreferences ?? [],
        partyRelationships:
          savedOrganisation.partyRelationships?.length ||
          savedOrganisation.members?.length ||
          !payload.partyRelationships?.length
            ? savedOrganisation.partyRelationships ??
              savedOrganisation.members ??
              []
            : payload.partyRelationships ?? [],
        members:
          savedOrganisation.members?.length ||
          savedOrganisation.partyRelationships?.length ||
          !payload.partyRelationships?.length
            ? savedOrganisation.members ??
              savedOrganisation.partyRelationships ??
              []
            : payload.partyRelationships ?? [],
      };

      const comparableSaved = toComparableOrganisation(safeSaved);

      setSuppressUnsavedPrompt(true);
      setOrganisationDetails(comparableSaved);
      setOriginalOrganisation(structuredClone(comparableSaved));
      setOriginalSelectedServiceIds([...selectedServiceIds]);
      setOriginalSelectedServiceRoles(structuredClone(selectedServiceRoles));
      setIsDirty(false);

      window.scrollTo({ top: 0, behavior: "smooth" });

      setTimeout(() => {
        onSaved?.(safeSaved.partyId);
        setSuppressUnsavedPrompt(false);
      }, 0);
    } catch (error) {
      console.error("Failed to save organisation", error);
      notify.error("Failed to save organisation");
    }
  };

  /* =========================
     Render
  ========================= */

  return (
    <>
      <Prompt
        when={isDirty && !suppressUnsavedPrompt}
        message="You have unsaved changes. Are you sure you want to leave?"
      />

      <div className="party-organisation-form">
        <div className="card">
          <div className="card-header">
            <div className="card-title">Organisation Details</div>
          </div>

          <div className="form-row">
            <div className="form-group form-group--full">
              <label className="form-label">
                Name{" "}
                <span className="form-required-marker" aria-hidden="true">
                  *
                </span>
              </label>
              {organisationOptions && onOrganisationSelect ? (
                <>
                  <input
                    className="form-input"
                    type="text"
                    name="displayName"
                    list={`organisation-options-${partyId ?? "new"}`}
                    value={organisationDetails.displayName}
                    onChange={(event) => {
                      handleInputChange(event);
                      const selectedOption = organisationOptions.find(
                        (option) => option.label === event.target.value,
                      );
                      if (
                        selectedOption &&
                        selectedOption.value !== selectedOrganisationId
                      ) {
                        onOrganisationSelect(selectedOption.value);
                      }
                    }}
                    autoComplete="off"
                    required
                    aria-required="true"
                    aria-label="Select or edit organisation name"
                  />
                  <datalist id={`organisation-options-${partyId ?? "new"}`}>
                    {organisationOptions.map((option) => (
                      <option key={option.value} value={option.label} />
                    ))}
                  </datalist>
                </>
              ) : (
                <input
                  className="form-input"
                  type="text"
                  name="displayName"
                  value={organisationDetails.displayName}
                  onChange={handleInputChange}
                  required
                  aria-required="true"
                />
              )}
            </div>

          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">
                Type{" "}
                <span className="form-required-marker" aria-hidden="true">
                  *
                </span>
              </label>
              <select
                name="organisationTypeId"
                className="form-input"
                value={organisationDetails.organisationTypeId || ""}
                onChange={handleInputChange}
                required
                aria-required="true"
              >
                <option value="">Select</option>

                {organisationTypes.map((ct) => (
                  <option
                    key={ct.organisationTypeId}
                    value={ct.organisationTypeId}
                  >
                    {ct.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Registration Number</label>
              <input
                className="form-input"
                type="text"
                name="registrationNumber"
                value={organisationDetails.registrationNumber ?? ""}
                onChange={handleInputChange}
              />
            </div>
          </div>

          {/* Notes */}
          <div className="form-row">
            <div className="form-group form-group--full">
              <label className="form-label">
                Notes{" "}
                <span className="form-required-marker" aria-hidden="true">
                  *
                </span>
              </label>
              <textarea
                className="form-input"
                name="notes"
                value={organisationDetails.notes ?? ""}
                onChange={handleInputChange}
                rows={4}
                required
                aria-required="true"
              />
            </div>
          </div>
        </div>

        <AddressCard
          partyId={organisationDetails.partyId}
          addresses={organisationDetails.partyAddresses ?? []}
          onChange={handleAddressChange}
          allowAddAnother={partyContext.mode === PartyFormMode.Admin}
        />

        <ContactCard
          contacts={organisationDetails.contactMethods ?? []}
          partyId={organisationDetails.partyId}
          onChange={handleContactChange}
          contactPreferences={organisationDetails.contactPreferences ?? []}
          onPreferenceChange={handleConsentChange}
          allowAddAnother={partyContext.mode === PartyFormMode.Admin}
        />

        {partyContext.allowPartySearch && showOrganisationMembers && (
          <OrganisationMembersCard
            partyId={organisationDetails.partyId}
            partyRelationships={organisationDetails.partyRelationships ?? []}
            onChange={handlePartyRelationshipsChange}
            relationshipApiBase={relationshipApiBase}
          />
        )}

        {partyContext.mode === PartyFormMode.Admin && showServiceEnrollment && (
          <ServiceEnrollmentCard
            partyId={formMode === "UPDATE" ? organisationDetails.partyId : null}
            tenantId={tenantId}
            selectedServices={selectedServiceIds}
            onSelectedServicesChange={setSelectedServiceIds}
            selectedServiceRoles={selectedServiceRoles}
            onSelectedServiceRolesChange={setSelectedServiceRoles}
            registrationServiceId={registrationServiceId}
            registrationServiceName={partyContext.serviceName}
            readonly={!partyContext.allowServiceSelection && !registrationServiceId}
          />
        )}

        {/* <ContactPreferenceCard
          contactPreferences={organisationDetails.contactPreferences ?? []}
          onChange={handleConsentChange}
        /> */}

        <div className="form-floating-actions">
          <button
            className={`form-submit-button ${
              isDirty ? "save-button-dirty" : ""
            }`}
            onClick={handleSave}
            disabled={!isDirty || loading}
          >
            {saveLabel}
          </button>

          {showCancel && (
            <button
              type="button"
              className="form-submit-button form-submit-button--secondary"
              onClick={onCancel}
            >
              {cancelLabel}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default PartyOrganisation;
